import React, { useState } from 'react';
import {
  BookOpen, Building2, Upload, Users, Shield, GitBranch, Search,
  ChevronRight, Check, ArrowRight, Database, Settings, Bell,
  FileText, MessageSquare, BarChart3, Lightbulb, AlertTriangle,
  Briefcase, Zap
} from 'lucide-react';
import './GuidePage.css';

const TABS = [
  { id: 'overview',    label: 'Quick Start',       icon: Zap },
  { id: 'pipeline',    label: 'Pipeline & CRM',    icon: GitBranch },
  { id: 'deals',       label: 'Deals',             icon: Building2 },
  { id: 'diligence',   label: 'Due Diligence',     icon: Shield },
  { id: 'documents',   label: 'Documents',         icon: FileText },
  { id: 'team',        label: 'Team & Permissions',icon: Users },
  { id: 'settings',    label: 'Settings',          icon: Settings },
];

function Section({ title, children }) {
  return (
    <div className="guide-section">
      <h2 className="guide-section-title">{title}</h2>
      {children}
    </div>
  );
}

function Step({ number, title, children }) {
  return (
    <div className="guide-step">
      <div className="guide-step-number">{number}</div>
      <div className="guide-step-body">
        <h4 className="guide-step-title">{title}</h4>
        <div className="guide-step-content">{children}</div>
      </div>
    </div>
  );
}

function Tip({ children }) {
  return (
    <div className="guide-tip">
      <Lightbulb size={15} className="guide-tip-icon" />
      <span>{children}</span>
    </div>
  );
}

function Warning({ children }) {
  return (
    <div className="guide-warning">
      <AlertTriangle size={15} className="guide-warning-icon" />
      <span>{children}</span>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description }) {
  return (
    <div className="guide-feature-card">
      <div className="guide-feature-icon"><Icon size={18} /></div>
      <div>
        <h4>{title}</h4>
        <p>{description}</p>
      </div>
    </div>
  );
}

// â”€â”€ Tab Content â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function OverviewTab() {
  return (
    <div>
      <div className="guide-hero-banner">
        <div className="guide-hero-badge"><Zap size={14} /> Welcome to DealNexa</div>
        <h1>Your Complete M&amp;A Platform Guide</h1>
        <p>
          DealNexa is a deal management platform designed for M&amp;A teams - private equity,
          corporate development, advisors, and anyone who buys or sells businesses.
          This guide walks you through everything from logging in to closing a deal.
        </p>
      </div>

      <Section title="What is DealNexa?">
        <p className="guide-para">
          DealNexa replaces the scattered tools most deal teams rely on - spreadsheets, email
          threads, Dropbox folders, and sticky notes - with one organized workspace. Everything
          you need to track, analyze, and close deals lives in one place.
        </p>
        <div className="guide-feature-grid">
          <FeatureCard icon={GitBranch}   title="Pipeline"        description="Track every deal from first contact to close with Kanban or list views." />
          <FeatureCard icon={Database}    title="CRM"             description="Import your existing deals from Excel, Salesforce, or HubSpot. Keep your full deal history." />
          <FeatureCard icon={Building2}   title="Deals"           description="Open a deal to run due diligence, manage documents, track tasks, and collaborate." />
          <FeatureCard icon={Shield}      title="Due Diligence"   description="AI reads your uploaded documents and surfaces risk flags, summaries, and key data." />
          <FeatureCard icon={FileText}    title="Documents"       description="Upload, annotate, and search every document associated with a deal." />
          <FeatureCard icon={Users}       title="Team"            description="Invite colleagues and advisors with role-based access. Full audit trail of every action." />
          <FeatureCard icon={MessageSquare} title="AI Chat"       description="Ask questions about your documents and get instant AI-powered answers." />
          <FeatureCard icon={Settings}    title="Settings"        description="Customize your home dashboard, manage permissions, and view the audit trail." />
        </div>
      </Section>

      <Section title="Getting started in 5 steps">
        <Step number={1} title="Create your organization">
          When you first sign up, DealNexa will walk you through a quick onboarding flow.
          You'll create an <strong>organization</strong> â€” this is your team's shared workspace.
          All deals, documents, and contacts are scoped to your organization, so teammates
          you invite will see the same data.
        </Step>
        <Step number={2} title="Invite your team">
          After creating your org, you can invite colleagues right from the onboarding screen
          or later from <strong>Members</strong> in the top-right menu. Assign them a role â€”
          Admin, Member, Viewer, or Custom â€” each with different levels of access.
        </Step>
        <Step number={3} title="Import your deals or create your first deal">
          If your team tracks deals in a spreadsheet, use the <strong>CRM</strong> module to
          import your data via CSV. DealNexa will auto-detect your columns. Alternatively,
          head to <strong>Deals</strong> and click <strong>+ New Deal</strong> to start from
          scratch.
        </Step>
        <Step number={4} title="Upload documents">
          Open any deal and go to the <strong>Documents</strong> tab, or use
          <strong> Upload Documents</strong> in the sidebar. DealNexa accepts PDFs and Word
          docs. Once uploaded, the AI automatically analyzes the document for key data,
          financial figures, and risk flags.
        </Step>
        <Step number={5} title="Run due diligence">
          Navigate to <strong>Due Diligence</strong> inside a deal to see AI-extracted findings,
          annotate documents, track what's missing, and generate summaries for IC memos or
          board reports.
        </Step>
        <Tip>You can always come back to this guide by clicking <strong>Getting Started</strong> at the bottom of the left sidebar.</Tip>
      </Section>

      <Section title="Navigation overview">
        <p className="guide-para">The left sidebar is your main navigation. Here's what each item does:</p>
        <div className="guide-nav-table">
          {[
            ['Home',             'Your dashboard â€” recent documents, deal status overview, and activity feed.'],
            ['Pipeline',         'Kanban board showing your active deals by stage. Drag cards between stages.'],
            ['CRM',              'Your full deal history, including past and imported deals. Import from CSV/Excel/Salesforce/HubSpot.'],
            ['Deals',            'Detailed deal management â€” open a deal to access diligence, docs, notes, tasks, and more.'],
            ['Due Diligence',    'AI-powered analysis of uploaded documents. Findings, risk flags, and financial summaries.'],
            ['Document Library', 'Browse all documents across deals. Search, filter, and annotate.'],
            ['Contacts',         'Manage buyers, sellers, advisors, lawyers, and other deal participants.'],
            ['Upload Documents', 'Upload PDFs and Word docs to run AI analysis.'],
            ['AI Chat',          'Chat with the AI about your documents. Ask anything about a filing, contract, or memo.'],
          ].map(([name, desc]) => (
            <div key={name} className="guide-nav-row">
              <span className="guide-nav-name">{name}</span>
              <span className="guide-nav-desc">{desc}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function PipelineTab() {
  return (
    <div>
      <Section title="Pipeline â€” Track deals stage by stage">
        <p className="guide-para">
          The Pipeline is a visual board (like a Kanban board) that shows all your active deals
          organized by where they are in the M&amp;A process. Think of it like a whiteboard
          with sticky notes that your whole team can see.
        </p>

        <Step number={1} title="Understanding stages">
          Deals move through stages from left to right. The default stages are:
          <ul className="guide-list">
            <li><strong>Level 1 Assessment</strong> â€” Initial screening and fit evaluation</li>
            <li><strong>Level 2 Assessment</strong> â€” Deeper look; management meetings, more info</li>
            <li><strong>LOI</strong> â€” Letter of Intent signed; exclusivity period typically begins</li>
            <li><strong>Final Agreements</strong> â€” Definitive documents being negotiated</li>
            <li><strong>Closed</strong> â€” Transaction completed</li>
            <li><strong>Post Close Integration</strong> â€” 100-day plan execution</li>
          </ul>
          <Tip>You can rename, reorder, or add custom stages from the pipeline customize panel (gear icon at top right of the pipeline page).</Tip>
        </Step>

        <Step number={2} title="Moving a deal between stages">
          <strong>Kanban view:</strong> Simply drag a deal card from one column to another.
          <br /><br />
          <strong>List view:</strong> Click the stage dropdown on a deal row and select the new stage.
          <br /><br />
          The change is saved instantly and logged in the audit trail so your team always knows
          what moved and when.
        </Step>

        <Step number={3} title="Filtering your pipeline">
          Use the search bar and filter dropdowns at the top of the pipeline page to narrow deals
          by owner, industry, or any keyword. This is helpful when you have a large pipeline and
          need to focus on specific deals.
        </Step>

        <Step number={4} title="Archived and Discontinued deals">
          If a deal falls out of your active pipeline, you can move it to <strong>Archived</strong>
          (deal on hold) or <strong>Discontinued</strong> (deal dead). These live in a separate
          view so they don't clutter your active board but are never lost.
        </Step>
        <Warning>Once a deal is deleted, it cannot be recovered. Use Archive or Discontinued for deals you may want to revisit.</Warning>
      </Section>

      <Section title="CRM â€” Your complete deal history">
        <p className="guide-para">
          The CRM module is where you keep your <em>full</em> deal record â€” including past deals,
          prospects you've screened, and data migrated from your existing tools. While the Pipeline
          shows active deals in progress, the CRM is the archive and source of truth for every
          deal your organization has ever touched.
        </p>

        <Step number={1} title="Importing from CSV, Excel, Salesforce, or HubSpot">
          <ol className="guide-ordered-list">
            <li>Open the <strong>CRM</strong> tab from the sidebar.</li>
            <li>Click <strong>Import Data</strong> at the top right.</li>
            <li>Drag and drop your file (CSV, XLSX) or click to browse.</li>
            <li>DealNexa reads your headers and <strong>auto-maps</strong> known columns
              (Company Name, Deal Value, Stage, Owner, etc.).</li>
            <li>Any columns it doesn't recognize become <strong>custom fields</strong> â€”
              they're preserved exactly as-is from your sheet.</li>
            <li>Preview the data, then click <strong>Import Records</strong>.</li>
          </ol>
          <Tip>If your file uses different column names, use the mapping dropdowns during import to tell DealNexa which column is which. Everything unmapped becomes a custom column.</Tip>
        </Step>

        <Step number={2} title="Pushing CRM deals into the active Pipeline">
          Once your deals are in the CRM, you can promote active ones to the Pipeline:
          <ol className="guide-ordered-list">
            <li>In the CRM table, check the box next to one or more deals.</li>
            <li>Click <strong>Add to Pipeline</strong> in the action bar that appears.</li>
            <li>Choose the starting stage and confirm.</li>
          </ol>
          The deal now appears on both the CRM and the Pipeline board. Status changes in the
          Pipeline are reflected back in the CRM record.
        </Step>

        <Step number={3} title="Creating a deal in both CRM and Pipeline at once">
          When you click <strong>+ New Deal</strong> from Deals or Pipeline, there is a checkbox
          labeled <strong>"Also add to CRM"</strong>. Check it to save the deal in both places
          simultaneously.
        </Step>
      </Section>
    </div>
  );
}

function DealsTab() {
  return (
    <div>
      <Section title="Deals â€” Your deal workspace">
        <p className="guide-para">
          Each deal in DealNexa has its own workspace with everything you need to manage the
          transaction from start to finish. Think of it like a digital deal room.
        </p>

        <Step number={1} title="Creating a new deal">
          <ol className="guide-ordered-list">
            <li>Click <strong>Deals</strong> in the sidebar.</li>
            <li>Click the <strong>+ New Deal</strong> button at the top right.</li>
            <li>Enter the deal name (usually the target company's name), industry,
              estimated deal value, target close date, and deal stage.</li>
            <li>Optionally assign an owner â€” the person on your team responsible for the deal.</li>
            <li>Click <strong>Create Deal</strong>.</li>
          </ol>
          <Tip>The deal value is used to calculate your total pipeline value shown on the home dashboard and pipeline overview.</Tip>
        </Step>

        <Step number={2} title="Inside a deal â€” the tabs">
          Once you open a deal, you'll see several tabs:
          <ul className="guide-list">
            <li><strong>Overview</strong> â€” Key deal stats, stage, value, target close, and notes.</li>
            <li><strong>Documents</strong> â€” Upload and view all documents for this deal.</li>
            <li><strong>Due Diligence</strong> â€” AI findings, risk flags, and checklists.</li>
            <li><strong>Tasks</strong> â€” Create to-do items and track what's done vs. pending.</li>
            <li><strong>Notes</strong> â€” Free-form notes for your team (with author and timestamp).</li>
            <li><strong>Contacts</strong> â€” People associated with this deal (sellers, advisors, lawyers).</li>
          </ul>
        </Step>

        <Step number={3} title="Changing deal status">
          You can move a deal to a new stage in two ways:
          <ul className="guide-list">
            <li>From the <strong>Pipeline</strong> page, drag the deal card to a new column.</li>
            <li>From inside the deal, click the <strong>Stage</strong> dropdown and select a new stage.</li>
          </ul>
          All stage changes are automatically logged with a timestamp and the name of the user
          who made the change.
        </Step>

        <Step number={4} title="Deleting a deal">
          Click the <strong>three-dot menu</strong> (or trash icon) on a deal card and confirm
          deletion. This is permanent â€” consider using <strong>Archive</strong> if you might need
          the deal again.
        </Step>
      </Section>
    </div>
  );
}

function DiligenceTab() {
  return (
    <div>
      <Section title="Due Diligence â€” AI-powered deal analysis">
        <p className="guide-para">
          DealNexa's due diligence tools help you move faster without cutting corners. Upload
          documents and the AI automatically extracts the key information, flags risks, and
          builds a picture of the deal â€” so your team can focus on judgment, not data entry.
        </p>

        <Step number={1} title="AI Findings">
          Navigate to <strong>Deals â†’ [your deal] â†’ Due Diligence</strong>, or click
          <strong> Due Diligence</strong> in the sidebar's Deals section.
          <br /><br />
          After uploading documents, the AI generates:
          <ul className="guide-list">
            <li><strong>Risk flags</strong> â€” items that may require negotiation or pose deal risk</li>
            <li><strong>Financial summaries</strong> â€” revenue, EBITDA, margins, trends</li>
            <li><strong>Key data extraction</strong> â€” contract terms, employee counts, customer concentration</li>
            <li><strong>Confidence scores</strong> â€” how certain the AI is about each finding</li>
          </ul>
          <Tip>Review AI findings critically. The AI is a first-pass analyst â€” it surfaces issues quickly, but your judgment on materiality and deal risk is what counts.</Tip>
        </Step>

        <Step number={2} title="Findings Notes">
          Use <strong>Findings Notes</strong> (sidebar under Due Diligence) to write your own
          analysis and observations alongside the AI's output. These notes are team-visible and
          timestamped.
        </Step>

        <Step number={3} title="Diligence Tracker">
          The Diligence Tracker shows the status of each diligence workstream (financial,
          legal, commercial, etc.) as a checklist. Mark items complete, assign owners, and
          track what's still open. This is what you'd bring into an IC or board update.
        </Step>

        <Step number={4} title="Synergy Engine">
          If you're evaluating a bolt-on acquisition or comparing multiple targets, the
          <strong> Synergy Engine</strong> lets you run side-by-side deal comparisons â€”
          revenue synergies, cost savings, headcount overlap, and more. The AI helps
          quantify synergy assumptions.
        </Step>
        <Warning>The AI reads the documents you upload. If a document is unclear, scanned poorly, or missing key pages, the AI may miss information. Always cross-check critical figures against the source document.</Warning>
      </Section>
    </div>
  );
}

function DocumentsTab() {
  return (
    <div>
      <Section title="Documents â€” Upload, analyze, and annotate">
        <p className="guide-para">
          Documents are at the center of every deal. DealNexa accepts PDFs and Word documents
          and runs AI analysis automatically after upload.
        </p>

        <Step number={1} title="Uploading a document">
          <ol className="guide-ordered-list">
            <li>Click <strong>Upload Documents</strong> in the sidebar, or open a deal and
              go to the <strong>Documents</strong> tab.</li>
            <li>Drag and drop your files, or click to browse.</li>
            <li>DealNexa will start processing the document. Depending on size, analysis
              takes between a few seconds and a couple of minutes.</li>
            <li>Once complete, you'll see the document in the <strong>Document Library</strong>
              with its analysis status.</li>
          </ol>
          <Tip>You can upload multiple documents at once. Each one is analyzed independently.</Tip>
        </Step>

        <Step number={2} title="Document Library">
          The <strong>Document Library</strong> (sidebar under Deals) shows all documents across
          all deals. You can filter by deal, status, or document type. Click any document to
          open it.
        </Step>

        <Step number={3} title="Summaries">
          Each analyzed document gets an AI-generated <strong>Summary</strong> â€” a concise
          overview of what the document says, key figures, and notable points. Find these under
          <strong> Summaries</strong> in the sidebar.
        </Step>

        <Step number={4} title="Annotations">
          Open any document and use the <strong>Annotation</strong> tool to highlight text and
          add notes. Annotations are saved and shared with your team â€” useful for flagging
          specific clauses, marking concerns, or highlighting important numbers.
        </Step>

        <Step number={5} title="AI Chat about a document">
          Navigate to <strong>AI Chat</strong> in the sidebar and ask questions about your
          uploaded documents in plain English. Examples:
          <ul className="guide-list">
            <li>"What is the revenue for fiscal year 2023?"</li>
            <li>"Are there any change of control provisions in the purchase agreement?"</li>
            <li>"What is the customer concentration risk?"</li>
            <li>"Summarize the key terms of the LOI."</li>
          </ul>
        </Step>
      </Section>
    </div>
  );
}

function TeamTab() {
  return (
    <div>
      <Section title="Team & Permissions â€” Collaborate securely">
        <p className="guide-para">
          DealNexa is built for teams. You can invite your colleagues, outside advisors,
          and lawyers with exactly the right level of access for each person.
        </p>

        <Step number={1} title="Inviting team members">
          <ol className="guide-ordered-list">
            <li>Click your name or avatar at the <strong>top right</strong> of the screen.</li>
            <li>Select <strong>Members</strong> from the dropdown.</li>
            <li>Click the <strong>Invite</strong> tab.</li>
            <li>Enter the person's email address and choose their role.</li>
            <li>Click <strong>Generate Link</strong> â€” this creates a shareable invite link.</li>
            <li>Copy the link and send it to the person. When they click it, they'll create
              an account and join your organization.</li>
          </ol>
        </Step>

        <Step number={2} title="Understanding roles">
          <div className="guide-roles-table">
            <div className="guide-role-row">
              <span className="guide-role-badge admin">Admin</span>
              <span>Full access â€” manage team members, change permissions, see audit trail, edit all data.</span>
            </div>
            <div className="guide-role-row">
              <span className="guide-role-badge member">Member</span>
              <span>Can view and edit deals, documents, and contacts. Cannot manage team or see audit trail.</span>
            </div>
            <div className="guide-role-row">
              <span className="guide-role-badge viewer">Viewer</span>
              <span>Read-only access. Can see everything but cannot create, edit, or delete anything.</span>
            </div>
            <div className="guide-role-row">
              <span className="guide-role-badge custom">Custom</span>
              <span>Fully configurable. Admins set exactly which actions this role can perform.</span>
            </div>
          </div>
          <Tip>Use the <strong>Custom</strong> role for outside advisors who need limited access â€” for example, a lawyer who needs to view documents but not edit deals.</Tip>
        </Step>

        <Step number={3} title="Changing a member's role">
          In the <strong>Members</strong> tab of Team Settings, admins can change any member's
          role using the dropdown next to their name. Changes take effect immediately.
        </Step>

        <Step number={4} title="Per-member permissions">
          For fine-grained control, go to <strong>Members â†’ Permissions â†’ Per Member</strong>.
          Here you can control exactly which resources (Pipeline, Deals, Documents, etc.) a
          specific person can view, edit, delete, or administer.
        </Step>

        <Step number={5} title="Removing a member">
          In the Members tab, click the <strong>trash icon</strong> next to a member's name.
          They will be removed from the organization and lose access immediately.
        </Step>
      </Section>
    </div>
  );
}

function SettingsTab() {
  return (
    <div>
      <Section title="Settings â€” Customize your experience">
        <p className="guide-para">
          Access Settings from the top-right user dropdown â†’ <strong>Settings</strong>.
        </p>

        <Step number={1} title="General">
          View your organization name, type, your role, and email. A summary of all
          platform features available to you.
        </Step>

        <Step number={2} title="Home Customization">
          The <strong>Home Customization</strong> tab lets you control which widgets appear
          on your home dashboard and in what order.
          <ul className="guide-list">
            <li>Toggle the <strong>Visible / Hidden</strong> button on any widget to show or hide it.</li>
            <li>Drag widgets by the <strong>grip handle</strong> on the left to reorder them.</li>
            <li>Click <strong>Save Layout</strong> when done. Your layout is personal â€” other
              team members keep their own layout.</li>
            <li>Click <strong>Reset to Default</strong> to go back to the original layout.</li>
          </ul>
        </Step>

        <Step number={3} title="Audit Trail (Admins only)">
          The <strong>Audit Trail</strong> is a complete log of everything that has happened
          in your organization â€” deals created, documents uploaded, members added, permissions
          changed, and more. Every event includes:
          <ul className="guide-list">
            <li>Date and time</li>
            <li>The user who performed the action</li>
            <li>What action was taken</li>
            <li>What resource was affected</li>
          </ul>
          Use the filters to search by user, action type, or date range. Only Admins can access
          this page by default; Admins can grant access to Custom-role users via Permissions.
          <Warning>The Audit Trail cannot be edited or deleted. It is a permanent record.</Warning>
        </Step>
      </Section>
    </div>
  );
}

const TAB_CONTENT = {
  overview:  <OverviewTab />,
  pipeline:  <PipelineTab />,
  deals:     <DealsTab />,
  diligence: <DiligenceTab />,
  documents: <DocumentsTab />,
  team:      <TeamTab />,
  settings:  <SettingsTab />,
};

const GuidePage = () => {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="guide-page">
      <div className="guide-header">
        <div className="guide-header-inner">
          <BookOpen size={22} />
          <h1>DealNexa Guide</h1>
        </div>
        <p className="guide-header-sub">
          Everything you need to know to get the most out of DealNexa.
        </p>
      </div>

      <div className="guide-layout">
        {/* Tab nav */}
        <nav className="guide-nav">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`guide-nav-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={15} />
                {tab.label}
                {activeTab === tab.id && <ChevronRight size={14} className="guide-nav-chevron" />}
              </button>
            );
          })}
        </nav>

        {/* Content */}
        <div className="guide-content">
          {TAB_CONTENT[activeTab]}
        </div>
      </div>
    </div>
  );
};

export default GuidePage;
